import { useState, useEffect, useCallback } from 'react'
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts'
import { getSalesVolumeData } from '../../supabase-client'
import DateRangeSelector from './DateRangeSelector'
import { getCurrentYear, createDate, parseDateString } from '../../utils/dateUtils.js'

// Custom tooltip with shadcn styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">
                {entry.dataKey === 'volume' ? 'Volume (kg)' : 'Value (£)'}
              </span>
            </div>
            <span className="font-medium">
              {entry.dataKey === 'volume'
                ? `${entry.value}kg`
                : `£${entry.value.toFixed(2)}`
              }
            </span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

export default function SalesVolumeChart({ dateRange: externalDateRange, loading: externalLoading, compact = false, salesData = [] }) {
  const [internalDateRange, setInternalDateRange] = useState('ytd')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  // Use external date range if provided, otherwise use internal
  const dateRange = externalDateRange || internalDateRange

  const filterDataByDateRange = useCallback((salesData, range) => {
    if (range === 'all') {
      return salesData
    }

    let startDate = new Date()
    switch (range) {
      case '1month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3)
        break
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6)
        break
      case '1year':
        startDate.setFullYear(startDate.getFullYear() - 1)
        break
      case 'ytd': {
        const currentYear = getCurrentYear()
        startDate = createDate(currentYear, 0, 1)
        break
      }
      default:
        startDate.setMonth(startDate.getMonth() - 6)
    }

    return salesData.filter(item => parseDateString((item.isoMonth || item.month) + '-01') >= startDate)
  }, [])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const salesData = await getSalesVolumeData(dateRange)
      setData(salesData)
    } catch (error) {
      console.error('Error loading sales volume data:', error)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    if (salesData && salesData.length > 0) {
      // Filter shared data based on date range
      const filteredData = filterDataByDateRange(salesData, dateRange)
      setData(filteredData)
    } else {
      // Fallback to individual data fetching if no shared data
      loadData()
    }
  }, [dateRange, salesData, filterDataByDateRange, loadData])

  const isLoading = externalLoading !== undefined ? externalLoading : loading
  if (isLoading) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-loading">
          Loading sales data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-loading">
          No sales data available for the selected period
        </div>
      </div>
    )
  }

  return (
    <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
      {!compact && !externalDateRange && (
        <div className="flex justify-between items-center mb-4">
          <div></div>
          <DateRangeSelector 
            value={dateRange} 
            onValueChange={setInternalDateRange}
            className="w-[140px]"
          />
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis 
            dataKey="month" 
            axisLine={false}
            tickLine={false}
            tick={{ 
              fontSize: 12, 
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          />
          <YAxis 
            yAxisId="volume"
            orientation="left"
            axisLine={false}
            tickLine={false}
            tick={{ 
              fontSize: 12, 
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{ 
              value: 'Volume (kg)', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
          />
          <YAxis 
            yAxisId="value"
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ 
              fontSize: 12, 
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{ 
              value: 'Value (£)', 
              angle: 90, 
              position: 'insideRight',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '12px',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          />
          <Bar 
            yAxisId="volume"
            dataKey="volume" 
            name="Sales Volume"
            fill="#F54A00" 
            radius={[4, 4, 0, 0]}
            opacity={0.8}
          />
          <Line 
            yAxisId="value"
            type="monotone" 
            dataKey="value" 
            name="Sale Value"
            stroke="#009689" 
            strokeWidth={3}
            dot={{ fill: '#009689', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: '#009689' }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}